import { useMemo } from "react";
import { useAdminFetcherWithSwr } from "../api";
import { SpotSearchQueryParams } from "../generatedApi/spotSearchEngineApi";
import { useURLSearchParams } from "./useURLSearchParams";

const DEDUPING_INTERVAL_MS = 2000;

interface UseSpotItemsQueryOptions {
  apiName: "spot_search_engine" | "admin_search_engine";
  operationId: "searchSpots" | "searchDraftSpots";
}

export const useSpotItemsQuery = (options: UseSpotItemsQueryOptions) => {
  const { apiName, operationId } = options;
  const { urlParams } = useURLSearchParams();

  const queryParams = useMemo((): SpotSearchQueryParams => {
    const {
      page,
      size,
      name,
      phonetic_name,
      lat,
      lon,
      sort_field,
      sort_order,
      published_revision_id,
      has_unpublished_changes
    } = urlParams;

    const apiParams: SpotSearchQueryParams = {
      page: Number(page) || 0,
      size: Number(size) || 10
    };

    if (name) apiParams.name = name;
    if (phonetic_name) apiParams.phonetic_name = phonetic_name;
    if (lat && lon) apiParams.location = { lat: Number(lat), lon: Number(lon) };
    if (sort_field && sort_order)
      apiParams.sort = [{ field: sort_field as any, order: sort_order as any }];
    if (published_revision_id) apiParams.published_revision_id = published_revision_id === "true";
    if (has_unpublished_changes)
      apiParams.has_unpublished_changes = has_unpublished_changes === "true";

    return apiParams;
  }, [urlParams]);

  const { data, isLoading, error, mutate } =
    operationId === "searchSpots"
      ? useAdminFetcherWithSwr({
          apiName: "spot_search_engine",
          serviceName: "default",
          operationId: "searchSpots",
          requestParameters: { requestBody: queryParams },
          swrConfiguration: {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            refreshInterval: 0,
            dedupingInterval: DEDUPING_INTERVAL_MS,
            keepPreviousData: true
          }
        })
      : useAdminFetcherWithSwr({
          apiName: "admin_search_engine",
          serviceName: "default",
          operationId: "searchDraftSpots",
          requestParameters: { requestBody: queryParams },
          swrConfiguration: {
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            refreshInterval: 0,
            dedupingInterval: DEDUPING_INTERVAL_MS,
            keepPreviousData: true
          }
        });

  return { data, loading: isLoading, error, mutate };
};
